import React, { useState } from "react";
import { InputNumber, Select, Switch, Button, Input, message } from "antd";
import aiConsoleService from "services/aiconsoleservice";

// antd v5 primary buttons render transparent under Tailwind Preflight; force the themed fill.
const PRIMARY_BTN = "!bg-[#0b1437] hover:!bg-[#1e293b] !border-[#0b1437] !text-white";

// Allowed models (label shown to admins). Keep in sync with config/ai.php 'models'.
const MODELS = [
  { value: "global.anthropic.claude-sonnet-4-5-20250929-v1:0", label: "Claude Sonnet 4.5" },
  { value: "anthropic.claude-haiku-4-5-20251001-v1:0", label: "Claude Haiku 4.5" },
];

const ERROR_BEHAVIORS = [
  { value: "retry_once", label: "Retry once, then fallback message" },
  { value: "fallback_message", label: "Show a fallback message" },
  { value: "fail_loud", label: "Fail loud (surface the error)" },
];

const Field = ({ label, hint, children }) => (
  <div className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <label className="block text-sm font-medium text-slate-700 dark:text-gray-200 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const AgentSettingsForm = ({ agent, onSaved }) => {
  const editable = agent?.status === "editable";
  const [form, setForm] = useState({
    model: agent?.model ?? MODELS[0].value,
    temperature: agent?.temperature ?? null,
    max_tokens: agent?.max_tokens ?? null,
    timeout_seconds: agent?.timeout_seconds ?? null,
    error_behavior: agent?.error_behavior ?? "fail_loud",
    is_active: agent?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!reason.trim()) {
      message.error("Please enter a reason for the change.");
      return;
    }
    setSaving(true);
    try {
      const res = await aiConsoleService.updateAgentSettings(agent.agent_key, { ...form, reason });
      message.success("Settings saved.");
      setReason("");
      onSaved?.(res?.data?.settings);
    } catch (e) {
      message.error("Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="agent-settings-form" className="max-w-2xl">
      {!editable && (
        <div className="mb-3 text-sm text-gray-400 dark:text-gray-500">
          This agent is managed in a later release. Settings are read-only.
        </div>
      )}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <Field label="Model">
          <Select
            data-testid="agent-model"
            className="w-full"
            disabled={!editable}
            value={form.model}
            options={MODELS}
            onChange={(v) => set("model", v)}
          />
        </Field>
        <Field label="Temperature" hint="0 = deterministic, 1 = most varied. Leave blank to use the default.">
          <InputNumber
            data-testid="agent-temperature"
            className="w-full"
            disabled={!editable}
            min={0}
            max={1}
            step={0.1}
            value={form.temperature}
            onChange={(v) => set("temperature", v)}
          />
        </Field>
        <Field label="Max tokens" hint="Maximum length of the reply (1 to 8192).">
          <InputNumber
            data-testid="agent-max-tokens"
            className="w-full"
            disabled={!editable}
            min={1}
            max={8192}
            value={form.max_tokens}
            onChange={(v) => set("max_tokens", v)}
          />
        </Field>
        <Field label="Timeout (seconds)" hint="How long to wait for a reply before applying the error handling below.">
          <InputNumber
            data-testid="agent-timeout"
            className="w-full"
            disabled={!editable}
            min={1}
            max={120}
            value={form.timeout_seconds}
            onChange={(v) => set("timeout_seconds", v)}
          />
        </Field>
        <Field label="Error handling" hint="What to do if the model call fails or times out.">
          <Select
            data-testid="agent-error-behavior"
            className="w-full"
            disabled={!editable}
            value={form.error_behavior}
            options={ERROR_BEHAVIORS}
            onChange={(v) => set("error_behavior", v)}
          />
        </Field>
        <Field label="Active" hint="Turn this agent on or off.">
          <Switch
            data-testid="agent-active"
            disabled={!editable}
            checked={!!form.is_active}
            onChange={(v) => set("is_active", v)}
          />
        </Field>
        {editable && (
          <Field label="Reason for change" hint="Required. Briefly say why (for the audit trail).">
            <Input.TextArea
              data-testid="agent-reason"
              rows={2}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        )}
      </div>
      {editable && (
        <Button
          data-testid="agent-settings-save"
          type="primary"
          className={`mt-4 ${PRIMARY_BTN}`}
          loading={saving}
          onClick={save}
        >
          Save settings
        </Button>
      )}
    </div>
  );
};

export default AgentSettingsForm;
